import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOutlet } from '../contexts/OutletContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  User,
  Calendar,
  RefreshCw,
  Eye,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';

export default function Shifts() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { currentOutlet } = useOutlet();
  const { formatCurrency } = useCurrency();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftDetailOpen, setShiftDetailOpen] = useState(false);
  const [shiftDetail, setShiftDetail] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchShifts();
  }, [currentOutlet?.id]);

  const fetchShifts = async () => {
    if (!currentOutlet?.id) return;
    
    setLoading(true);
    try {
      const params = { outlet_id: currentOutlet.id };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const resp = await api.get('/shifts', { params });
      setShifts(resp.data);
    } catch (error) {
      toast.error('Failed to fetch shifts');
    } finally {
      setLoading(false);
    }
  };

  const viewShiftDetail = async (shift) => {
    setSelectedShift(shift);
    setShiftDetailOpen(true);
    
    try {
      const resp = await api.get(`/shifts/${shift.id}/summary`);
      setShiftDetail(resp.data);
    } catch (error) {
      toast.error('Failed to load shift details');
    }
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (openedAt, closedAt) => {
    if (!openedAt) return '-';
    const start = new Date(openedAt);
    const end = closedAt ? new Date(closedAt) : new Date();
    const diffMs = end - start;
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Calculate totals
  const totalDifference = shifts.filter(s => s.status === 'CLOSED').reduce((sum, s) => sum + (s.difference || 0), 0);
  const shiftsWithDiscrepancy = shifts.filter(s => s.status === 'CLOSED' && Math.abs(s.difference || 0) > 0).length;

  return (
    <div className="p-6 space-y-6" data-testid="shifts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Clock className="h-6 w-6" />
            Shift Reports
          </h1>
          <p className="text-muted-foreground">View cash shift history and reconciliation</p>
        </div>
        <Button variant="outline" onClick={fetchShifts} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Shifts</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{shifts.length}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Shifts with Discrepancy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${shiftsWithDiscrepancy > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
              {shiftsWithDiscrepancy}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Cash Difference</CardTitle>
          </CardHeader>
          <CardContent>
            <p className={`text-2xl font-bold ${totalDifference < 0 ? 'text-red-500' : totalDifference > 0 ? 'text-green-500' : ''}`}>
              {formatCurrency(totalDifference)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex gap-4 items-end">
            <div>
              <Label>From</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="w-40"
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="w-40"
              />
            </div>
            <Button onClick={fetchShifts}>Filter</Button>
            <Button variant="outline" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Shifts Table */}
      <Card>
        <CardHeader>
          <CardTitle>Shift History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : shifts.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No shifts found</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cashier</TableHead>
                  <TableHead>Opened</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Opening</TableHead>
                  <TableHead className="text-right">Expected</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Diff</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shifts.map((shift) => (
                  <TableRow key={shift.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {shift.cashier_name}
                      </div>
                    </TableCell>
                    <TableCell>{formatDateTime(shift.opened_at)}</TableCell>
                    <TableCell>{formatDuration(shift.opened_at, shift.closed_at)}</TableCell>
                    <TableCell>
                      <Badge variant={shift.status === 'OPEN' ? 'default' : 'secondary'}>
                        {shift.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(shift.opening_cash)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {shift.status === 'CLOSED' ? formatCurrency(shift.closing_cash_expected) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {shift.status === 'CLOSED' ? formatCurrency(shift.closing_cash_actual) : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {shift.status === 'CLOSED' ? (
                        <span className={`font-mono font-medium ${
                          shift.difference < 0 ? 'text-red-500' : 
                          shift.difference > 0 ? 'text-green-500' : ''
                        }`}>
                          {shift.difference > 0 ? '+' : ''}{formatCurrency(shift.difference)}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => viewShiftDetail(shift)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Shift Detail Modal */}
      <Dialog open={shiftDetailOpen} onOpenChange={setShiftDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Shift Detail</DialogTitle>
          </DialogHeader>
          
          {selectedShift && (
            <div className="space-y-4">
              {/* Status Banner */}
              <div className={`p-3 rounded-lg flex items-center gap-2 ${
                selectedShift.status === 'OPEN' ? 'bg-green-500/10 text-green-500' :
                Math.abs(selectedShift.difference || 0) > 0 ? 'bg-yellow-500/10 text-yellow-500' :
                'bg-blue-500/10 text-blue-500'
              }`}>
                {selectedShift.status === 'OPEN' ? (
                  <><Clock className="h-5 w-5" /> Shift is currently OPEN</>
                ) : Math.abs(selectedShift.difference || 0) > 0 ? (
                  <><AlertTriangle className="h-5 w-5" /> Shift closed with discrepancy</>
                ) : (
                  <><CheckCircle2 className="h-5 w-5" /> Shift closed - balanced</>
                )}
              </div>

              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Cashier</Label>
                  <p className="font-medium">{selectedShift.cashier_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge>{selectedShift.status}</Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Opened</Label>
                  <p className="font-medium">{formatDateTime(selectedShift.opened_at)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Closed</Label>
                  <p className="font-medium">{formatDateTime(selectedShift.closed_at)}</p>
                </div>
              </div>

              {/* Cash Summary */}
              <div className="border rounded-lg p-4 space-y-2">
                <h4 className="font-semibold mb-3">Cash Summary</h4>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Opening Cash</span>
                  <span className="font-mono">{formatCurrency(selectedShift.opening_cash)}</span>
                </div>
                {shiftDetail && (
                  <>
                    <div className="flex justify-between text-green-500">
                      <span>+ Cash Sales</span>
                      <span className="font-mono">{formatCurrency(shiftDetail.cash_sales)}</span>
                    </div>
                    <div className="flex justify-between text-red-500">
                      <span>- Cash Refunds</span>
                      <span className="font-mono">{formatCurrency(shiftDetail.cash_refunds)}</span>
                    </div>
                  </>
                )}
                <div className="border-t pt-2 flex justify-between font-semibold">
                  <span>Expected Cash</span>
                  <span className="font-mono">{formatCurrency(selectedShift.closing_cash_expected)}</span>
                </div>
                {selectedShift.status === 'CLOSED' && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Actual Cash (counted)</span>
                      <span className="font-mono">{formatCurrency(selectedShift.closing_cash_actual)}</span>
                    </div>
                    <div className={`flex justify-between font-bold ${
                      selectedShift.difference < 0 ? 'text-red-500' :
                      selectedShift.difference > 0 ? 'text-green-500' : ''
                    }`}>
                      <span>Difference</span>
                      <span className="font-mono">
                        {selectedShift.difference > 0 ? '+' : ''}{formatCurrency(selectedShift.difference)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {/* Discrepancy Reason */}
              {selectedShift.difference_reason && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <Label className="text-yellow-500">Discrepancy Reason</Label>
                  <p className="mt-1">{selectedShift.difference_reason}</p>
                </div>
              )}

              {/* Stats */}
              {shiftDetail && (
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold">{shiftDetail.orders_count}</p>
                    <p className="text-sm text-muted-foreground">Orders</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{shiftDetail.transactions_count}</p>
                    <p className="text-sm text-muted-foreground">Transactions</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatCurrency(shiftDetail.total_sales)}</p>
                    <p className="text-sm text-muted-foreground">Total Sales</p>
                  </div>
                </div>
              )}

              {/* Payment Breakdown */}
              {shiftDetail?.payment_breakdown && Object.keys(shiftDetail.payment_breakdown).length > 0 && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Payment Methods</h4>
                  {Object.entries(shiftDetail.payment_breakdown).map(([method, data]) => (
                    <div key={method} className="flex justify-between py-1">
                      <span className="text-muted-foreground">{method} ({data.count}x)</span>
                      <span className="font-mono">{formatCurrency(data.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
